import { PageHeader, Empty, Loading } from '../../components/office';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { menuApi, menuSectionsApi, modifiersApi } from '@verlo/shared/services/api';
import MenuItemDialog from '../../components/menu/MenuItemDialog';
import HoursEditor, { hoursWords, normaliseHours } from '../../components/menu/HoursEditor';
import { saveMenuItem } from '../../components/menu/saveMenuItem';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Switch } from '@verlo/shared/components/ui/switch';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { toast } from 'sonner';
import { ChevronRight, GripVertical, Plus, Trash2, Check, X } from 'lucide-react';
import useDragList from '../../components/drag/useDragList';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@verlo/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import { useSite, useAuthoringSite } from '../../contexts/SiteContext';
import { sectionName } from '../../lib/sectionName';

// Structure (menu consolidation, wave 2): the till's sections, in the order the till shows
// them, and the items in each. Reorder by dragging a handle, or with the arrow keys on it
// (the shared drag, Back office sweep Oct 2026); rename
// in place; switch a section off and its items leave the till at the next sync. A section
// nobody has saved is here too, with no mark (Chris's review M4: its dishes are on the till) —
// touching it saves it.

const gbp = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);

const MenuStructurePage = () => {
  // One site's structure, following the header's picker. Sections are site-scoped rows, so
  // a rename here must reach only this site's items — two venues may both run a "Mains".
  const { site } = useSite();
  // Reading follows the picker; *writing* needs a venue named. With two sites and none
  // picked there is nothing honest to send, so the add control says so rather than posting
  // a section the server will refuse for want of a `?site=` this page cannot supply.
  const authoring = useAuthoringSite();
  const [sections, setSections] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});          // section name → expanded
  const [renaming, setRenaming] = useState(null); // { id, name }
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [deleting, setDeleting] = useState(null); // { sec, to } while asking where its dishes go
  // A dish opens here, over Structure, so Save and Cancel leave the section as it was: open,
  // and scrolled where it was. It used to go to Items (Back office sweep, Oct 2026).
  const [editing, setEditing] = useState(null);
  const [modifiers, setModifiers] = useState(null);
  const openItem = (it) => {
    setEditing(it);
    // As Items does: the live modifiers only, and this site's (the cache clears with the site).
    if (!modifiers) modifiersApi.getAll(site).then((r) => setModifiers((r.data || []).filter((x) => x.is_active !== false))).catch(() => setModifiers([]));
  };

  const load = async () => {
    try {
      const [s, i] = await Promise.all([menuSectionsApi.list(site), menuApi.getAll(undefined, site)]);
      setSections(s.data);
      setItems(i.data);
    } catch {
      toast.error("Couldn't load the menu");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [site]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setModifiers(null); }, [site]);

  const itemsIn = useMemo(() => {
    const m = {};
    for (const it of items) (m[it.category] ||= []).push(it);
    return m;
  }, [items]);

  // Every write goes through the API and re-reads, so the page never shows an order the
  // Cloud does not hold.
  const run = async (fn, ok) => {
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save that");
    }
  };

  // A derived section has no id yet. The first action on any of them saves *all* of them in
  // the order shown — otherwise the one touched would jump ahead of the rest, since saved
  // sections list before unsaved ones. `orderedIds` is then the whole list, saved.
  const orderedIds = async () => {
    const list = sectionsRef.current;
    if (list.every((s) => s.id)) return list.map((s) => s.id);
    const ids = [];
    for (const s of list) ids.push(s.id || (await menuSectionsApi.create({ name: s.name }, authoring.site)).data.id);
    await menuSectionsApi.reorder(ids, site);
    // Saved now: later moves in the same burst must not create them again.
    sectionsRef.current = list.map((s, i) => ({ ...s, id: ids[i] }));
    return ids;
  };
  const ensureSaved = async (sec) => {
    if (sec.id) return sec.id;
    const ids = await orderedIds();
    return ids[sectionsRef.current.findIndex((s) => s.name === sec.name)];
  };

  // A new order of sections, by name: shown at once, then saved (unsaved ones first become rows).
  // One save at a time: holding an arrow key moves a section many times a second, and
  // overlapping saves would come back out of order (and save unsaved sections twice). Moves
  // made while one is saving wait, and only the latest order is sent.
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const pendingOrder = useRef(null);
  const pendingItems = useRef(null);
  const savingItems = useRef(false);
  const savingOrder = useRef(false);
  const placeSections = (names) => {
    setSections((ss) => names.map((n) => ss.find((x) => x.name === n)));
    pendingOrder.current = names;
    if (savingOrder.current) return;
    savingOrder.current = true;
    run(async () => {
      while (pendingOrder.current) {
        const want = pendingOrder.current;
        pendingOrder.current = null;
        const ids = await orderedIds();
        const byName = Object.fromEntries(sectionsRef.current.map((x, i) => [x.name, ids[i]]));
        await menuSectionsApi.reorder(want.map((n) => byName[n]), site);
      }
    }).finally(() => {
      savingOrder.current = false;
      if (pendingOrder.current) placeSections(pendingOrder.current);
    });
  };
  const sectionDrag = useDragList(sections, (x) => x.name, placeSections);
  const nudgeSection = (sec, delta) => {
    const names = sections.map((x) => x.name);
    const from = names.indexOf(sec.name);
    const to = from + delta;
    if (to < 0 || to >= names.length) return;
    names.splice(from, 1); names.splice(to, 0, sec.name);
    placeSections(names);
  };

  const toggleSection = (sec, on) => run(async () => {
    const id = await ensureSaved(sec);
    await menuSectionsApi.update(id, { is_active: on }, site);
  }, on ? `${sectionName(sec.name)} is on the till` : `${sectionName(sec.name)} is off the till`);

  // What the till does with this section's dishes while they're off for their hours (Oct
  // 2026): greyed with their hours, or left off until they're on.
  const setWhenOff = (sec, whenOff) => run(async () => {
    const id = await ensureSaved(sec);
    await menuSectionsApi.update(id, { when_off: whenOff }, site);
  }, whenOff === 'hide' ? `${sec.name}: off dishes are hidden on the till` : `${sec.name}: off dishes show greyed on the till`);

  // A section's hours (Oct 2026): Sunday roasts on Sundays only. They were stored and the
  // till kept them, but nothing in the office could set them.
  const setHours = (sec, hours) => run(async () => {
    const id = await ensureSaved(sec);
    await menuSectionsApi.update(id, { availability: normaliseHours(hours) }, site);
  }, normaliseHours(hours).length ? `${sectionName(sec.name)} has its hours` : `${sectionName(sec.name)} is on whenever you are`);

  const rename = (sec) => run(async () => {
    const name = renaming.name.trim();
    if (!name || name === sec.name) { setRenaming(null); return; }
    const id = await ensureSaved(sec);
    await menuSectionsApi.update(id, { name }, site);
    setRenaming(null);
  }, `Renamed to ${renaming?.name?.trim()} . Its items moved with it.`);

  // Any section can go (Back office sweep, Oct 2026). One with dishes asks where they move first.
  const askRemove = (sec) => {
    const n = (itemsIn[sec.name] || []).length;
    setDeleting({ sec, to: n ? (sections.find((x) => x.name !== sec.name)?.name || '') : '' });
  };
  const removeSection = () => {
    const { sec, to } = deleting;
    const n = (itemsIn[sec.name] || []).length;
    setDeleting(null);
    run(async () => {
      // The section its dishes go to must be a saved one: the server only moves them into a
      // section it holds. Saving the list saves both it and the one being deleted.
      const target = sections.find((x) => x.name === to);
      if (n && !target?.id) await orderedIds();
      const id = await ensureSaved(sec);
      await menuSectionsApi.delete(id, site, n ? to : undefined);
    }, n ? `${sec.name} deleted; its ${n} dish${n === 1 ? '' : 'es'} moved to ${to}` : `${sec.name} deleted`);
  };

  const addSection = () => {
    const name = newName.trim();
    if (!name) return;
    run(() => menuSectionsApi.create({ name }, authoring.site), `${name} added`).then(() => { setNewName(''); setAdding(false); });
  };

  // A section's dishes in a new order: shown at once, then saved.
  const placeItems = (ids) => {
    setItems((all) => {
      // The section's dishes take their new places; every other dish stays where it is.
      const queue = ids.map((id) => all.find((x) => x.id === id));
      const moving = new Set(ids);
      return all.map((x) => (moving.has(x.id) ? queue.shift() : x));
    });
    // One save at a time, as for sections: the latest order waits for the one being sent.
    pendingItems.current = ids;
    if (savingItems.current) return;
    savingItems.current = true;
    run(async () => {
      while (pendingItems.current) {
        const want = pendingItems.current;
        pendingItems.current = null;
        await menuApi.reorder(want, site);
      }
    }).finally(() => { savingItems.current = false; if (pendingItems.current) placeItems(pendingItems.current); });
  };

  const onTill = sections.filter((s) => s.is_active !== false).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Structure"
        purpose="The sections the till shows, in the order it shows them, and what is in each."
        actions={(
          <Button className="bg-verlo hover:bg-verlo-hover" onClick={() => setAdding(true)} disabled={!authoring.ready} title={authoring.reason || undefined} data-testid="add-section-btn">
            <Plus size={16} className="mr-2" />
            Add section
          </Button>
        )}
      />

      {loading ? <Loading shape="table" /> : sections.length === 0 && !adding ? (
        <Empty word="Sections" title="No sections yet" testId="structure-empty"
          action={<Button variant="outline" onClick={() => setAdding(true)}>Add the first</Button>}>
          Add an item on Items and its section appears here; or add sections first and put items in them.
        </Empty>
      ) : (
        <>
          <Notice tone="info" word="Order" title={`${onTill} of ${sections.length} sections on the till`}>
            The till shows sections top to bottom in this order, and items in the order within each. Changes reach every till at the next sync.
          </Notice>

          <div className="overflow-hidden rounded-lg border border-hairline bg-white" data-testid="structure-list" {...sectionDrag.listProps}>
            {sectionDrag.rows.map((sec, idx) => {
              const secItems = itemsIn[sec.name] || [];
              const expanded = !!open[sec.name];
              const isRenaming = renaming && renaming.key === sec.name;
              return (
                <div
                  key={sec.name}
                  className={`border-b border-hairline last:border-0 transition-colors ${sectionDrag.isHeld(sec) ? 'bg-verlo-tint outline-dashed outline-1 -outline-offset-1 outline-verlo/50 [&>*]:opacity-60' : ''}`}
                  data-testid={`section-${sec.name}`}
                  {...sectionDrag.rowProps(sec, idx)}
                >
                  <div className={`flex items-center gap-2 px-3 py-2 ${sec.is_active === false && !sectionDrag.isHeld(sec) ? 'bg-surface-2' : ''}`}>
                    <button
                      type="button"
                      className="cursor-grab rounded p-1 text-ink-faint hover:bg-hover-tint hover:text-ink active:cursor-grabbing"
                      {...sectionDrag.handleProps(sec)}
                      onKeyDown={(e) => { if (e.key === 'ArrowUp') { e.preventDefault(); nudgeSection(sec, -1); } if (e.key === 'ArrowDown') { e.preventDefault(); nudgeSection(sec, 1); } }}
                      aria-label={`Move ${sec.name}: drag it, or use the arrow keys`}
                      data-testid={`section-handle-${sec.name}`}
                    >
                      <GripVertical size={16} />
                    </button>
                    <button type="button" className="flex min-w-0 items-center gap-2 text-left" onClick={() => setOpen({ ...open, [sec.name]: !expanded })} aria-expanded={expanded} data-testid={`section-toggle-${sec.name}`}>
                      <ChevronRight size={14} className={`shrink-0 text-ink-faint transition-transform ${expanded ? 'rotate-90' : ''}`} />
                      {!isRenaming && (
                        <span className={`font-heading text-[15px] font-bold ${sec.is_active === false ? 'text-ink-soft' : 'text-ink'}`}>{sectionName(sec.name)}</span>
                      )}
                    </button>
                    {isRenaming && (
                      <span className="flex items-center gap-1">
                        <Input value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} className="h-8 w-56" autoFocus
                               onKeyDown={(e) => { if (e.key === 'Enter') rename(sec); if (e.key === 'Escape') setRenaming(null); }} data-testid="section-rename-input" />
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => rename(sec)} aria-label="Save name" data-testid="section-rename-save"><Check size={14} /></Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setRenaming(null)} aria-label="Cancel"><X size={14} /></Button>
                      </span>
                    )}
                    <span className="font-mono text-[12.5px] tabular-nums text-ink-faint">{secItems.length}</span>
                    {sec.is_active === false && <Signal tone="stop" dot={false}>off the till</Signal>}
                    {hoursWords(sec.availability) && <span className="text-[12.5px] text-ink-soft" data-testid={`section-hours-words-${sec.name}`}>{hoursWords(sec.availability)}</span>}
                    {sec.is_active !== false && sec.when_off === 'hide' && <Signal tone="info" dot={false}>hidden when off</Signal>}
                    <span className="flex-1" />
                    {!isRenaming && (
                      <Button size="sm" variant="ghost" className="h-8 text-[12.5px] text-ink-soft" onClick={() => setRenaming({ key: sec.name, name: sec.name })} data-testid={`section-rename-${sec.name}`}>Rename</Button>
                    )}
                    <label className="flex items-center gap-2 text-[12.5px] text-ink-soft">
                      On till
                      <Switch checked={sec.is_active !== false} onCheckedChange={(v) => toggleSection(sec, v)} data-testid={`section-on-${sec.name}`} />
                    </label>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-signal-stop hover:text-signal-stop disabled:opacity-30" onClick={() => askRemove(sec)} disabled={sections.length < 2 && secItems.length > 0}
                            title={`Delete ${sec.name}`} aria-label={`Delete ${sec.name}`} data-testid={`section-delete-${sec.name}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>

                  {/* Open sections fold while a section is dragged, so it moves past them in one go. */}
                  {expanded && !sectionDrag.held && (
                    <SectionItems sec={sec} items={secItems} onOpen={openItem} onPlace={placeItems} onWhenOff={(v) => setWhenOff(sec, v)} onHours={(h) => setHours(sec, h)} />
                  )}
                </div>
              );
            })}

            {adding && (
              <div className="flex items-center gap-2 border-t border-hairline bg-surface-2 px-3 py-2" data-testid="section-add-row">
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Section name" className="h-8 w-64" autoFocus
                       onKeyDown={(e) => { if (e.key === 'Enter') addSection(); if (e.key === 'Escape') { setAdding(false); setNewName(''); } }} data-testid="section-add-input" />
                <Button size="sm" className="h-8 bg-verlo hover:bg-verlo-hover" onClick={addSection} data-testid="section-add-save">Add</Button>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => { setAdding(false); setNewName(''); }}>Cancel</Button>
              </div>
            )}
          </div>
        </>
      )}
      <Dialog open={!!deleting} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <DialogContent className="max-w-sm" data-testid="section-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete {deleting?.sec.name}?</DialogTitle>
            <DialogDescription>
              {(itemsIn[deleting?.sec.name] || []).length
                ? `Its ${(itemsIn[deleting?.sec.name] || []).length} dishes move to another section first, so nothing leaves the till.`
                : 'It has no dishes in it.'}
            </DialogDescription>
          </DialogHeader>
          {(itemsIn[deleting?.sec.name] || []).length > 0 && (
            <div className="space-y-1.5">
              <span className="text-[12.5px] text-ink-soft">Move its dishes to</span>
              <Select value={deleting.to} onValueChange={(v) => setDeleting({ ...deleting, to: v })}>
                <SelectTrigger data-testid="section-delete-to"><SelectValue /></SelectTrigger>
                <SelectContent>{sections.filter((x) => x.name !== deleting.sec.name).map((x) => <SelectItem key={x.name} value={x.name}>{x.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button className="bg-signal-stop text-white hover:bg-signal-stop/90" onClick={removeSection} disabled={(itemsIn[deleting?.sec.name] || []).length > 0 && !deleting?.to} data-testid="section-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <MenuItemDialog
        item={editing}
        sections={sections}
        modifiers={modifiers || []}
        menuItems={items}
        onClose={() => setEditing(null)}
        onSave={async (form) => { await saveMenuItem(form, authoring.site); setEditing(null); load(); }}
      />
    </div>
  );
};

// One section's dishes, in their own drag list: a dish moves within its section.
function SectionItems({ sec, items, onOpen, onPlace, onWhenOff, onHours }) {
  // Only a section with hours somewhere in it has dishes that go off, so only it asks.
  const timed = (sec.availability || []).length > 0 || items.some((it) => (it.availability || []).length > 0);
  const drag = useDragList(items, (it) => it.id, onPlace);
  const nudge = (it, delta) => {
    const ids = items.map((x) => x.id);
    const from = ids.indexOf(it.id);
    const to = from + delta;
    if (to < 0 || to >= ids.length) return;
    ids.splice(from, 1); ids.splice(to, 0, it.id);
    onPlace(ids);
  };
  return (
    <div className="border-t border-hairline bg-surface-2/60 px-3 py-2" data-testid={`section-items-${sec.name}`} {...drag.listProps}>
      {items.length === 0 ? (
        <p className="px-8 py-2 text-[12.5px] text-ink-faint">Nothing in this section. <Link to={`/back-office/menu?section=${encodeURIComponent(sec.name)}`} className="text-verlo hover:underline">Add an item</Link>.</p>
      ) : drag.rows.map((it, i) => (
        <div
          key={it.id}
          className={`flex items-center gap-2 rounded-md px-1 py-1 transition-colors ${drag.isHeld(it) ? 'bg-verlo-tint outline-dashed outline-1 -outline-offset-1 outline-verlo/50 [&>*]:opacity-60' : 'hover:bg-white'}`}
          data-testid={`structure-item-${it.id}`}
          {...drag.rowProps(it, i)}
        >
          <button type="button" className="ml-5 cursor-grab rounded p-1 text-ink-faint hover:bg-white hover:text-ink active:cursor-grabbing"
            {...drag.handleProps(it)}
            onKeyDown={(e) => { if (e.key === 'ArrowUp') { e.preventDefault(); nudge(it, -1); } if (e.key === 'ArrowDown') { e.preventDefault(); nudge(it, 1); } }}
            aria-label={`Move ${it.name}: drag it, or use the arrow keys`} data-testid={`item-handle-${it.id}`}>
            <GripVertical size={14} />
          </button>
          <button type="button" onClick={() => onOpen(it)} className={`min-w-0 flex-1 truncate text-left text-[13.5px] hover:underline ${it.is_available === false ? 'text-ink-faint line-through' : 'text-ink'}`} data-testid={`structure-open-${it.id}`}>{it.name}</button>
          {it.promoted && <Signal tone="info" dot={false}>promoted</Signal>}
          <span className="font-mono text-[12.5px] tabular-nums text-ink-soft">{gbp(it.price)}</span>
        </div>
      ))}
      <SectionHours sec={sec} onSave={onHours} />
      {timed && (
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-hairline px-8 pt-2" data-testid={`section-when-off-${sec.name}`}>
          <span className="text-[12.5px] text-ink-soft">When a dish here is off for its hours, the till</span>
          <Segmented value={sec.when_off === 'hide' ? 'hide' : 'show'} onChange={(v) => v !== (sec.when_off || 'show') && onWhenOff(v)}
            options={[{ value: 'show', label: 'Shows it greyed' }, { value: 'hide', label: 'Hides it' }]} />
        </div>
      )}
    </div>
  );
}

// The section's own hours: every dish in it is on at these times, on top of any hours of its own.
function SectionHours({ sec, onSave }) {
  const [draft, setDraft] = useState(null);   // null = not editing
  const words = hoursWords(sec.availability);
  if (draft === null) {
    return (
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-hairline px-8 pt-2" data-testid={`section-hours-${sec.name}`}>
        <span className="text-[12.5px] text-ink-soft">{words ? `On the till: ${words}` : 'On the till whenever you are open'}</span>
        <Button size="sm" variant="ghost" className="h-8 text-[12.5px] text-verlo" onClick={() => setDraft(sec.availability || [])} data-testid={`section-hours-change-${sec.name}`}>{words ? 'Change hours' : 'Set hours'}</Button>
      </div>
    );
  }
  return (
    <div className="mt-1 space-y-3 border-t border-hairline px-8 py-3" data-testid={`section-hours-edit-${sec.name}`}>
      <HoursEditor value={draft} onChange={setDraft} what="this section"
        words={{ always: 'On whenever you are. Add a time to make it a lunch menu, a weekend brunch or Sunday roasts.',
                 set: 'Every dish in this section is on the till at these times. Bills already open are untouched.' }} />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
        <Button size="sm" className="bg-verlo hover:bg-verlo-hover" onClick={async () => { await onSave(draft); setDraft(null); }} data-testid={`section-hours-save-${sec.name}`}>Save hours</Button>
      </div>
    </div>
  );
}

export default MenuStructurePage;
